import React, { useState } from 'react';
import { Space, Spin, Progress } from 'antd';
import { Radio, DatePicker, Select, Empty } from '@maxtropy/components';
import dayjs from 'dayjs';
import { InstantAvgIndicator, useInstantAvgAll } from '../../hooks/useInstantAvgAll';
import SubTitle from '@/components/SubTitle';
import gasIcon from '@/assets/icons/supply-selected@2x.webp';
import waterIcon from '@/assets/icons/water-selected@2x.webp';
import styles from './index.module.scss';
import { keepTwoAndWanOrLine, keepTwoNull } from '@/utils/utils';
import { V2EnvMonitorConfigGetPostResponse } from '@maxtropy/device-customer-apis-v2';

interface IndicatorCardsProps {
  gasDevices: V2EnvMonitorConfigGetPostResponse['exhaustDevices'];
  waterDevices: V2EnvMonitorConfigGetPostResponse['wastewaterDevices'];
  refreshKey: number;
}

const IndicatorCards: React.FC<IndicatorCardsProps> = ({ gasDevices = [], waterDevices = [], refreshKey }) => {
  const [selectedGasDeviceId, setSelectedGasDeviceId] = useState(gasDevices![0]?.deviceId);
  const [selectedWaterDeviceId, setSelectedWaterDeviceId] = useState(waterDevices![0]?.deviceId);
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));

  const { gasData, waterData, loading } = useInstantAvgAll({
    gasDeviceId: selectedGasDeviceId,
    waterDeviceId: selectedWaterDeviceId,
    date: selectedDate,
    refreshKey,
  });

  // Sync default selection when devices change
  React.useEffect(() => {
    if (gasDevices.length && !selectedGasDeviceId) {
      setSelectedGasDeviceId(gasDevices![0].deviceId);
    }
    if (waterDevices.length && !selectedWaterDeviceId) {
      setSelectedWaterDeviceId(waterDevices![0].deviceId);
    }
  }, [gasDevices, waterDevices, selectedGasDeviceId, selectedWaterDeviceId]);

  const renderIndicatorCard = (indicator: InstantAvgIndicator) => {
    const { upperLimit, ratio, hourlyAvg, propertyName, propertyId, unit } = indicator;

    return (
      <div className={styles.indicatorCard} key={propertyId}>
        <div className={styles.cardHeader}>
          {propertyName}({unit})
        </div>
        <div className={styles.cardContent}>
          <div className={styles.progressWrapper}>
            <Progress
              type="circle"
              showInfo={false}
              percent={Math.min(ratio ?? 0, 100)}
              trailColor="rgba(74, 144, 226, 0.10)"
              strokeColor={{ '0%': '#92ED79', '100%': '#59D744' }}
              size={60}
              strokeWidth={8}
            />
            <div className={styles.progressInner}>{keepTwoNull(ratio)}%</div>
          </div>
          <div className={styles.valueInfo}>
            <div className={styles.avgValue}>
              <span className={styles.label}>小时均值</span>
              <span className={styles.valueNumber}>{keepTwoAndWanOrLine(hourlyAvg)}</span>
            </div>
            <div className={styles.limitValue}>
              <span className={styles.label}>小时均值上限</span>
              <span className={styles.limitNumber}>{keepTwoAndWanOrLine(upperLimit)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPanel = (
    title: string,
    icon: string,
    devices:
      | V2EnvMonitorConfigGetPostResponse['exhaustDevices']
      | V2EnvMonitorConfigGetPostResponse['wastewaterDevices'],
    selectedDeviceId: number,
    onDeviceChange: (id: number) => void,
    indicators?: InstantAvgIndicator[]
  ) => (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelHeaderLeft}>
          <img src={icon} alt="" width={32} />
          <span className={styles.panelTitle}>{title}</span>
        </div>
        <Select
          placeholder="选择设备"
          value={selectedDeviceId}
          onChange={onDeviceChange}
          style={{ width: 160 }}
          size="small"
          options={devices!.map(d => ({ label: d.deviceName, value: d.deviceId }))}
          allowClear={false}
        />
      </div>
      {indicators?.length ? (
        <div className={styles.cardGrid}>{indicators.map(renderIndicatorCard)}</div>
      ) : (
        <Empty description="暂无指标数据" style={{ margin: '40px auto' }} />
      )}
    </div>
  );

  return (
    <SubTitle
      title={
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 26 }}>
          <span>环保数据指标</span>
          <Space size={8}>
            <Radio.Group optionType="button" buttonStyle="solid" defaultValue={1}>
              <Radio.Button value={1}>日</Radio.Button>
            </Radio.Group>
            <DatePicker
              value={dayjs(selectedDate)}
              onChange={date => date && setSelectedDate(date.format('YYYY-MM-DD'))}
              allowClear={false}
            />
          </Space>
        </div>
      }
    >
      <Spin spinning={loading}>
        <div className={styles.panelsRow}>
          {renderPanel(
            '废气指标',
            gasIcon,
            gasDevices,
            selectedGasDeviceId!,
            setSelectedGasDeviceId,
            gasData?.indicators
          )}
          {renderPanel(
            '废水指标',
            waterIcon,
            waterDevices,
            selectedWaterDeviceId!,
            setSelectedWaterDeviceId,
            waterData?.indicators
          )}
        </div>
      </Spin>
    </SubTitle>
  );
};

export default IndicatorCards;
